<template>
  <v-tooltip
    v-if="compressed"
    right
    :color="color"
    content-class="caption"
    transition="slide-x-transition"
    :disabled="chiptipDisabled"
  >
    <template v-slot:activator="{ on }">
      <v-chip
        v-on="on"
        label
        class="squeeze mr-1 mb-1"
        v-if="compressed"
        :color="color"
        small
        @click.stop="selected"
      >
        <span v-if="isFilm" class="mr-1">
          <v-img v-if="image" :src="image" width="48"></v-img>
          <v-icon v-else>{{ icon }}</v-icon>
        </span>
        <v-avatar class="ml-1 mr-1" v-else>
          <v-img v-if="image" :src="image"></v-img>
          <v-icon v-else>{{ icon }}</v-icon>
        </v-avatar>
        <span :class="textSizeClass">&nbsp;{{ text }}</span>
      </v-chip>
    </template>

    <v-card flat :color="color">
      <v-card-actions class="pl-0 pb-0">
        <v-chip
          label
          class="squeeze mr-1 mb-1"
          v-if="compressed"
          :color="color"
          small
        >
          <span v-if="isFilm" class="mr-1">
            <v-img v-if="image" :src="image" width="48"></v-img>
            <v-icon v-else>{{ icon }}</v-icon>
          </span>
          <v-avatar v-else>
            <v-img v-if="image" :src="image"></v-img>
            <v-icon v-else>{{ icon }}</v-icon>
          </v-avatar>
          <span :class="textSizeClass">&nbsp;{{ text }}</span>
        </v-chip>
      </v-card-actions>

      <v-card
        flat
        :color="color"
        v-for="ms in matchDetailSummaries"
        :key="ms.text"
      >
        <v-card-title class="caption font-weight-bold pb-0 pt-0 pl-1 pr-1">{{
          ms.text
        }}</v-card-title>
        <v-card-text class="caption pb-1 pt-0 pr-2 pl-2">
          <div v-for="(mh, j) in ms.highlights" :key="j" v-html="mh"></div>
        </v-card-text>
      </v-card>
    </v-card>
  </v-tooltip>

  <span v-else @click.stop="selected">
    <v-img
      class="pa-0 mt-n8 ml-n2"
      v-if="stripReason"
      :src="stripReason"
      :max-height="stripHeight"
      :max-width="stripWidth"
    ></v-img>
    <span class="font-weight-bold">{{ text }}</span>
    <span v-if="awards">
      <award-blob
        v-for="(award, index) in awards"
        :key="index"
        :award="award"
      ></award-blob>
    </span>
    <div v-for="(sto, index) in story" :key="index">
      <v-tooltip
        right
        :color="color"
        content-class="caption"
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <div v-on="on" :class="storyHeightClass">
            <story :story="sto"></story>
          </div>
        </template>
        <v-card :color="color" flat>
          <v-card-text>
            <story :story="sto"></story>
          </v-card-text>
        </v-card>
      </v-tooltip>
    </div>
  </span>
</template>
<script>
/* this will get called to display a reason
 * if its compressed, that'll be in the form of a chip
 * otherwise it'll be 2 td elements of a tr row of a table
 * or 1 if there's no imagecolumn required
 * image-icon will take care of choosing beetween an image if its available or an icon if not
 * avatar prop decides whether to present in the form of an avatar or a plain image
 * the selected click will take to the main page for that reason - for example the film page
 * the page it routes to is reponsible for collecting the full detail for that page
 * but passing the item reason here will give it something to get on with in case it has to make an api fetch
 * the reason passed from here is the same format as one of the reasons returned by the API
 */
import { settings, getIcon, getIconColor } from '@/js/store'
import { SearchUtils } from '@/js'
import Awardblob from '@/components/Awardblob'
import Story from '@/components/Story'
import { getTypeDefinition } from '@/shared/searcherfields'
import { mergeControls } from '@/js/apollo'
export default {
  name: 'Reason',
  props: {
    emit: String,
    reason: Object,
    compressed: Boolean,
    story: Array,
    itemType: String,
    forceColor: String,
    clickable: {
      type: Boolean,
      default: true,
    },
    hideTip: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    story: Story,
    'award-blob': Awardblob,
  },
  methods: {
    stopPlaying() {
      mergeControls({
        playing: '',
      })
    },
    selected() {
      if (this.clickable) {
        this.stopPlaying()
        SearchUtils.routeChange({ router: this.$router, reason: this.reason })
      } else if (this.emit) {
        this.$emit(this.emit, { reason: this.reason })
      }
    },
    getMatchDetailHighlight(item) {
      const labelType = this.getMatchDetailLabelType(item)
      const html =
        item &&
        item.highlight &&
        item.highlight
          .replace(/<em>/g, '<span class="fid-match-highlight">')
          .replace(/<\/em>/g, '</span>')
      return labelType && labelType === 'transcript'
        ? `<span class="font-italic">${html}</span>`
        : html
    },
    getMatchDetailKey(item) {
      if (!item || !item.key) return null
      return this.allFields && this.allFields[item.key]
    },
    getMatchDetailKeyText(item) {
      const k = this.getMatchDetailKey(item)
      return k && k.text
    },
    getMatchDetailLabelType(item) {
      if (!item || !item.labelType) return null
      return item.labelType
    },
  },
  computed: {
    stripHeight() {
      return 20
    },
    stripWidth () {
      return 200
    },
    stripReason() {
      return this.reason && this.reason.stripReason
    },
    bigEnough() {
      return this.$vuetify.breakpoint.mdAndUp
    },
    textSizeClass() {
      return this.$vuetify.breakpoint.smAndUp ? 'caption' : 'fid-xs-small-text'
    },
    storyHeightClass() {
      return ' caption fid-story-height-sm' //.. --<think I always want not >// this.bigEnough ? ' caption' : ' caption fid-story-height-sm'
    },
    color() {
      return getIconColor(this.forceColor || this.matchType)
    },
    typeDefinition() {
      return this.itemType ? getTypeDefinition({ type: this.itemType }) : ''
    },
    allFields() {
      return this.typeDefinition && this.typeDefinition.allFields
    },
    chiptipDisabled() {
      return !this.matchDetails || !this.matchDetails.length || this.hideTip
    },
    matchDetails() {
      return this.reason.MatchDetails
    },
    matchType() {
      return this.reason.matchType
    },
    matchID() {
      return this.reason.matchID
    },
    matchDetailSummaries() {
      if (!this.matchDetails) return null
      // summarize by titles
      const ob = this.matchDetails.reduce((p, c) => {
        let text = this.getMatchDetailKeyText(c)
        const labelType = this.getMatchDetailLabelType(c)
        if (labelType) {
          text += `(${labelType})`
        }
        if (!p[text]) {
          p[text] = {
            text,
            highlights: [],
          }
        }
        p[text].highlights.push(this.getMatchDetailHighlight(c))
        return p
      }, {})
      return Object.keys(ob).map((k) => ob[k])
    },
    icon() {
      return getIcon(this.matchType)
    },
    isFilm() {
      return this.matchType === 'film' || this.matchType === 'vimeofilm'
    },
    awards() {
      return SearchUtils.getAwardsFromReason({
        reason: this.reason,
      })
    },
    image() {
      return SearchUtils.getImageFromReason({
        reason: this.reason,
        minHeight: settings.dimensions.posterFrameHeight,
      })
    },
    text() {
      return SearchUtils.getBigTextFromReason({
        reason: this.reason,
        maxListShow: settings.maxListShow,
      })
    },
  },
}
</script>
